import { useState, useEffect } from 'react';
import moment from 'moment';
import { addDays, startOfDay, endOfDay, subMonths } from 'date-fns';
import { DefinedRange, DateRange, createStaticRanges } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { enGB } from 'date-fns/locale';
import CustomInput from '../CustomInput';
import { HorizontalLine } from '../Svg/Svg';

export default function CustomDatepicker(props) {
    const [start, setStart] = useState({
        dateString: `${moment(props.savedStartDate).format('DD MMM yyyy')}`,
        dateObject: props.savedStartDate,
    });
    const [end, setEnd] = useState({
        dateString: `${moment(props.savedEndDate).format('DD MMM yyyy')}`,
        dateObject: props.savedEndDate,
    });
    const [state, setState] = useState([
        {
            startDate: props.savedStartDate || new Date(),
            endDate: props.savedEndDate || new Date(),
            key: 'selection',
        },
    ]);
    const [isCustom, setIsCustom] = useState(false);
    const [months, setMonths] = useState(1);
    const [inputStartDateValue, setInputStartDateValue] = useState('');
    const [inputEndDateValue, setInputEndDateValue] = useState('');

    useEffect(() => {
        if (
            moment(end.dateObject).format('M') !==
            moment(start.dateObject).format('M')
        ) {
            setMonths(2);
        }
    }, [end.dateObject, start.dateObject]);

    const setCustom = () => {
        setIsCustom(true);
        setMonths(2);
    };

    const onChange = (item) => {
        if (
            moment(item.selection.endDate).format('M') !==
            moment(item.selection.startDate).format('M')
        ) {
            setMonths(2);
        }

        setStart({
            dateString: `${moment(item.selection.startDate).format(
                'DD MMM yyyy',
            )}`,
            dateObject: item.selection.startDate,
        });
        setEnd({
            dateString: `${moment(item.selection.endDate).format(
                'DD MMM yyyy',
            )}`,
            dateObject: item.selection.endDate,
        });
        setState([item.selection]);
    };

    const onApplyDate = () => {
        if (
            isCustom &&
            isValidDate(inputStartDateValue) &&
            isValidDate(inputEndDateValue)
        ) {
            const isStartDateBeforeEndDate = moment(
                inputStartDateValue,
                'DD-MM-YYYY',
            ).isBefore(moment(inputEndDateValue, 'DD-MM-YYYY'));

            const dateString = `${moment(
                isStartDateBeforeEndDate
                    ? inputStartDateValue
                    : inputEndDateValue,
                'DD-MM-YYYY',
            ).format('DD MMM yyyy')} - ${moment(
                isStartDateBeforeEndDate
                    ? inputEndDateValue
                    : inputStartDateValue,
                'DD-MM-YYYY',
            ).format('DD MMM yyyy')}`;

            props.getDate(dateString, {
                start: moment(inputStartDateValue, 'DD-MM-YYYY').toDate(),
                end: moment(inputEndDateValue, 'DD-MM-YYYY').toDate(),
            });

            onChange({
                selection: {
                    startDate: moment(
                        inputStartDateValue,
                        'DD-MM-YYYY',
                    ).toDate(),
                    endDate: moment(inputEndDateValue, 'DD-MM-YYYY').toDate(),
                },
            });
            props.setIsShown();
        } else {
            props.getDate(`${start.dateString} - ${end.dateString}`, {
                start: start.dateObject,
                end: end.dateObject,
            });
            props.setIsShown();
        }
    };

    const isValidDate = (date) => {
        return moment(date, 'DD-MM-YYYY').isValid();
    };

    const onInputChange = (e) => {
        if (e.target.id === 'start') {
            setInputStartDateValue(e.target.value);
        }
        if (e.target.id === 'end') {
            setInputEndDateValue(e.target.value);
        }
    };

    const onDefinedRageChange = (item) => {
        setStart({
            dateString: `${moment(item.selection.startDate).format(
                'DD MMM yyyy',
            )}`,
            dateObject: item.selection.startDate,
        });
        setEnd({
            dateString: `${moment(item.selection.endDate).format(
                'DD MMM yyyy',
            )}`,
            dateObject: item.selection.endDate,
        });
        setState([item.selection]);
    };

    const renderStaticRangeLabel = () => {
        return (
            <span
                className="datepicker__custom-static-range-label"
                onClick={setCustom}
            >
                Custom
            </span>
        );
    };

    return (
        <div className={`datepicker__wrapper ${isCustom ? 'isCustom' : ''}`}>
            <div>
                <DateRange
                    locale={enGB}
                    onChange={onChange}
                    ranges={state}
                    months={months}
                    direction="horizontal"
                    showMonthAndYearPickers={false}
                    showDateDisplay={false}
                />
            </div>
            <div className="datepicker__sidebar-wrapper">
                <div style={{ flexGrow: 1 }}>
                    <p className="datepicker__title">Date range</p>
                    <DefinedRange
                        onChange={onDefinedRageChange}
                        ranges={state}
                        renderStaticRangeLabel={renderStaticRangeLabel}
                        staticRanges={createStaticRanges([
                            {
                                label: 'Today',
                                range: () => ({
                                    startDate: startOfDay(new Date()),
                                    endDate: endOfDay(new Date()),
                                }),
                            },
                            {
                                label: 'Last week',
                                range: () => ({
                                    startDate: startOfDay(
                                        addDays(new Date(), -7),
                                    ),
                                    endDate: endOfDay(new Date()),
                                }),
                            },
                            {
                                label: 'Last month',
                                range: () => ({
                                    startDate: subMonths(new Date(), 1),
                                    endDate: endOfDay(new Date()),
                                }),
                            },
                            {
                                label: 'Custom',
                                range: () => ({
                                    startDate: addDays(new Date(), -2),
                                    endDate: new Date(),
                                }),
                                hasCustomRendering: true,
                            },
                        ])}
                        inputRanges={[]}
                    />
                    {isCustom && (
                        <div className="datepicker__input-wrapper">
                            <CustomInput
                                id="start"
                                placeholder={`${moment(
                                    addDays(new Date(), -2),
                                ).format('DD/MM/yyyy')}`}
                                onInputChange={onInputChange}
                                inputValue={inputStartDateValue}
                                labelText="From"
                                width="116px"
                            />
                            <span
                                style={{
                                    display: 'flex',
                                }}
                            >
                                &nbsp;&nbsp;
                                <HorizontalLine />
                                &nbsp;&nbsp;
                            </span>

                            <CustomInput
                                id="end"
                                placeholder={`${moment(new Date()).format(
                                    'DD/MM/yyyy',
                                )}`}
                                onInputChange={onInputChange}
                                inputValue={inputEndDateValue}
                                labelText="To"
                                width="116px"
                            />
                        </div>
                    )}
                </div>
                <div className="datepicker__buttons-wrapper">
                    <button
                        className="details-table__button modal__button-cancel"
                        onClick={props.setIsShown}
                    >
                        Cancel
                    </button>
                    <button
                        className="details-table__button modal__button-accept"
                        type="submit"
                        form="edit-client-form"
                        onClick={onApplyDate}
                    >
                        Apply
                    </button>
                </div>
            </div>
        </div>
    );
}
